import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export default function EmailAuth() {
  const { t, setUser } = useApp();
  const navigate = useNavigate();
  const [stage, setStage] = useState("email"); // email | password | otp | setup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [channel, setChannel] = useState("sms");
  const [busy, setBusy] = useState(false);

  const channelMsg = (ch) => (ch === "manual" ? t("login.manualCode") : ch === "email" ? t("login.sentEmail") : t("login.sentSms"));

  const showErr = (e) => {
    const d = e.response?.data?.detail;
    if (typeof d === "string") return toast.error(d);
    if (Array.isArray(d) && d[0]?.msg) return toast.error(d.map((x) => x.msg).join("; "));
    const status = e.response?.status;
    toast.error(status ? `${t("msg.error")} (HTTP ${status}) — riprova tra qualche istante` : `${t("msg.error")} — connessione non disponibile`);
  };

  const postWithRetry = async (url, data, attempts = 3) => {
    let lastErr;
    for (let i = 0; i < attempts; i++) {
      try {
        return await api.post(url, data);
      } catch (e) {
        lastErr = e;
        const status = e.response?.status;
        const gatewayError = !e.response || [502, 503, 504].includes(status) ||
          (typeof e.response?.data === "string" && e.response.data.includes("Bad gateway"));
        if (!gatewayError || i === attempts - 1) throw e;
        await new Promise((r) => setTimeout(r, 1500));
      }
    }
    throw lastErr;
  };

  const start = async () => {
    setBusy(true);
    try {
      const r = await postWithRetry("/auth/pwd/start", { email });
      setChannel(r.data.channel || "sms");
      if (r.data.needs_setup) {
        toast.success(channelMsg(r.data.channel));
        setStage("setup");
      } else {
        setStage("password");
      }
    } catch (e) { showErr(e); } finally { setBusy(false); }
  };

  const login = async () => {
    setBusy(true);
    try {
      const r = await postWithRetry("/auth/pwd/login", { email, password });
      setChannel(r.data.channel || "sms");
      toast.success(channelMsg(r.data.channel));
      setStage("otp");
    } catch (e) { showErr(e); } finally { setBusy(false); }
  };

  const finish = (u) => {
    setUser(u);
    navigate("/dashboard", { replace: true });
  };

  const verify = async () => {
    setBusy(true);
    try {
      const r = await postWithRetry("/auth/pwd/verify", { email, code, channel });
      finish(r.data);
    } catch (e) { showErr(e); } finally { setBusy(false); }
  };

  const setup = async () => {
    setBusy(true);
    try {
      const r = await postWithRetry("/auth/pwd/setup", { email, code, channel, password: newPassword });
      finish(r.data);
    } catch (e) { showErr(e); } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try {
      const r = await postWithRetry("/auth/pwd/resend", { email, channel });
      toast.success(channelMsg(channel));
    } catch (e) { showErr(e); } finally { setBusy(false); }
  };

  const forgotStart = async () => {
    setBusy(true);
    try {
      const r = await postWithRetry("/auth/pwd/reset/start", { email });
      setChannel(r.data.channel || "sms");
      toast.success(channelMsg(r.data.channel));
      setStage("reset");
    } catch (e) { showErr(e); } finally { setBusy(false); }
  };

  const resetConfirm = async () => {
    setBusy(true);
    try {
      const r = await postWithRetry("/auth/pwd/reset/confirm", { email, code, channel, password: newPassword });
      finish(r.data);
    } catch (e) { showErr(e); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="email-auth-form">
      <div>
        <Label>{t("login.email")}</Label>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          disabled={stage !== "email"} data-testid="email-auth-email-input" />
      </div>

      {stage === "email" && (
        <Button className="w-full" onClick={start} disabled={busy || !email.includes("@")} data-testid="email-auth-continue-button">
          {busy ? t("common.loading") : t("login.continue")}
        </Button>
      )}

      {stage === "password" && (
        <>
          <div>
            <Label>{t("login.password")}</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="email-auth-password-input" />
          </div>
          <Button className="w-full" onClick={login} disabled={busy || !password} data-testid="email-auth-login-button">
            {busy ? t("common.loading") : t("login.login")}
          </Button>
          <button type="button" className="w-full text-center text-xs text-primary hover:underline transition-colors"
            onClick={forgotStart} disabled={busy} data-testid="email-auth-forgot-link">
            {t("login.forgot")}
          </button>
        </>
      )}

      {stage === "otp" && (
        <>
          <p className="text-xs text-muted-foreground">{channelMsg(channel)}</p>
          <div>
            <Label>{t("login.otp")}</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6}
              placeholder="••••••" className="font-mono text-center text-lg tracking-widest" data-testid="email-auth-otp-input" />
          </div>
          <Button className="w-full" onClick={verify} disabled={busy || code.length < 6} data-testid="email-auth-verify-button">
            {busy ? t("common.loading") : t("login.verify")}
          </Button>
        </>
      )}

      {(stage === "setup" || stage === "reset") && (
        <>
          <p className="text-xs text-muted-foreground">
            {stage === "reset" ? t("login.resetHelp") : t("login.firstAccess")} {channelMsg(channel)}
          </p>
          <div>
            <Label>{t("login.otp")}</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6}
              placeholder="••••••" className="font-mono text-center text-lg tracking-widest" data-testid="email-auth-setup-otp-input" />
          </div>
          <div>
            <Label>{t("login.newPassword")}</Label>
            <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="email-auth-newpassword-input" />
          </div>
          <Button className="w-full" onClick={stage === "reset" ? resetConfirm : setup} disabled={busy || code.length < 6 || newPassword.length < 8} data-testid="email-auth-setup-button">
            {busy ? t("common.loading") : (stage === "reset" ? t("login.resetBtn") : t("login.setupBtn"))}
          </Button>
        </>
      )}

      {stage !== "email" && (
        <>
          <Button variant="outline" size="sm" className="w-full" onClick={resend} disabled={busy} data-testid="email-auth-resend-button">
            {t("login.resend")}
          </Button>
          <Button variant="ghost" size="sm" className="w-full" onClick={() => { setStage("email"); setCode(""); setPassword(""); }} data-testid="email-auth-back-button">
            ← {t("common.cancel")}
          </Button>
        </>
      )}
    </div>
  );
}
